import React, { useState } from 'react';
import { clsx } from 'clsx';
import { Coffin } from '../../types';
import { Modal, Input } from './index';
import toast from 'react-hot-toast';
import { CheckCircleIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';

export interface SaleData {
  quantity: number;
  clientId?: number;
  walkInName?: string;
  walkInPhone?: string;
  paymentMethod: string;
  notes?: string;
}

export interface SaleResult {
  id: number;
  paymentId?: number | null;
}

interface ClientOption {
  id: number;
  name: string;
  lastname?: string;
  cedula?: string;
}

interface QuickSellModalProps {
  isOpen: boolean;
  coffin: Coffin | null;
  clients?: ClientOption[];
  onConfirm: (data: SaleData) => Promise<SaleResult | void>;
  onDownloadReceipt?: (saleId: number) => Promise<void>;
  onCancel: () => void;
}

type ModalStep = 'confirm' | 'success';

const PAYMENT_METHODS = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA_BANCARIA', label: 'Transferencia bancaria' },
  { value: 'TRANSFERENCIA_MOVIL', label: 'Transferencia móvil' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'DEPOSITO_BANCARIO', label: 'Depósito bancario' }
];

const QuickSellModal: React.FC<QuickSellModalProps> = ({
  isOpen,
  coffin,
  clients = [],
  onConfirm,
  onDownloadReceipt,
  onCancel
}) => {
  const [step, setStep] = useState<ModalStep>('confirm');
  const [quantity, setQuantity] = useState(1);
  const [buyerMode, setBuyerMode] = useState<'walkin' | 'client'>('walkin');
  const [clientId, setClientId] = useState<number | ''>('');
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('EFECTIVO');
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [saleData, setSaleData] = useState<SaleData | null>(null);
  const [saleResult, setSaleResult] = useState<SaleResult | null>(null);

  if (!coffin) return null;

  const handleConfirmSale = async () => {
    if (quantity < 1) {
      toast.error('La cantidad debe ser al menos 1');
      return;
    }

    if (quantity > coffin.stock) {
      toast.error(`Stock insuficiente. Disponible: ${coffin.stock}`);
      return;
    }

    if (buyerMode === 'client' && !clientId) {
      toast.error('Seleccione un cliente');
      return;
    }

    if (buyerMode === 'walkin' && !clientName.trim()) {
      toast.error('Ingrese el nombre del comprador');
      return;
    }

    if (!paymentMethod) {
      toast.error('Seleccione el método de pago');
      return;
    }

    const data: SaleData = {
      quantity,
      paymentMethod,
      notes: notes || undefined,
      ...(buyerMode === 'client'
        ? { clientId: Number(clientId) }
        : {
            walkInName: clientName.trim(),
            walkInPhone: clientPhone.trim() || undefined
          })
    };

    try {
      setIsLoading(true);
      const result = await onConfirm(data);
      setSaleData(data);
      if (result && typeof result === 'object' && 'id' in result) {
        setSaleResult(result as SaleResult);
      }
      setStep('success');
    } catch (error) {
      toast.error('Error al procesar la venta');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReset = () => {
    setStep('confirm');
    setQuantity(1);
    setBuyerMode('walkin');
    setClientId('');
    setClientName('');
    setClientPhone('');
    setPaymentMethod('EFECTIVO');
    setNotes('');
    setSaleData(null);
    setSaleResult(null);
    onCancel();
  };

  const handleSellAnother = () => {
    setStep('confirm');
    setQuantity(1);
    setBuyerMode('walkin');
    setClientId('');
    setClientName('');
    setClientPhone('');
    setPaymentMethod('EFECTIVO');
    setNotes('');
    setSaleData(null);
    setSaleResult(null);
  };

  const handleDownload = async () => {
    if (!saleResult?.id || !onDownloadReceipt) return;
    try {
      await onDownloadReceipt(saleResult.id);
    } catch (error) {
      toast.error('No se pudo descargar el recibo');
      console.error(error);
    }
  };

  const totalAmount = coffin.price * quantity;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleReset}
      title={step === 'confirm' ? 'Confirmar venta' : 'Venta registrada'}
      size="sm"
    >
      {step === 'confirm' && (
        <div className="space-y-5">
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <h4 className="font-semibold text-gray-900 text-lg mb-2">{coffin.name}</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
              <div>
                <span className="text-gray-500">Tamaño:</span>{' '}
                <span className="font-medium text-gray-900">{coffin.size}</span>
              </div>
              <div>
                <span className="text-gray-500">Material:</span>{' '}
                <span className="font-medium text-gray-900">{coffin.material}</span>
              </div>
            </div>
            <div className="text-2xl font-semibold text-gray-900">
              ${Number(coffin.price).toLocaleString()}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Cantidad</label>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="h-10 w-10 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 font-bold text-lg flex items-center justify-center"
              >
                −
              </button>
              <input
                type="number"
                value={quantity}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  if (!Number.isNaN(val)) setQuantity(Math.max(1, Math.min(coffin.stock, val)));
                }}
                min={1}
                max={coffin.stock}
                className="flex-1 h-10 text-center font-bold border-2 border-gray-300 rounded-lg focus:border-gray-900 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => setQuantity(Math.min(coffin.stock, quantity + 1))}
                disabled={quantity >= coffin.stock}
                className="h-10 w-10 rounded-lg bg-gray-200 hover:bg-gray-300 disabled:opacity-50 font-bold text-lg flex items-center justify-center"
              >
                +
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Disponible: {coffin.stock} unidades</p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex justify-between items-baseline">
              <span className="text-gray-700 font-medium">Total</span>
              <span className="text-2xl font-semibold text-gray-900">
                ${totalAmount.toLocaleString()}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Método de pago</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <div className="flex gap-2 mb-3">
              <button
                type="button"
                onClick={() => setBuyerMode('walkin')}
                className={clsx(
                  'flex-1 py-2 rounded-lg text-sm font-medium border',
                  buyerMode === 'walkin'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                )}
              >
                Cliente ocasional
              </button>
              <button
                type="button"
                onClick={() => setBuyerMode('client')}
                className={clsx(
                  'flex-1 py-2 rounded-lg text-sm font-medium border',
                  buyerMode === 'client'
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-700 border-gray-300'
                )}
              >
                Cliente registrado
              </button>
            </div>

            {buyerMode === 'walkin' ? (
              <>
                <Input
                  placeholder="Nombre del comprador *"
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  className="mb-2"
                />
                <Input
                  placeholder="Teléfono"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                />
              </>
            ) : (
              <select
                value={clientId}
                onChange={(e) => setClientId(e.target.value ? Number(e.target.value) : '')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900/10"
              >
                <option value="">Seleccione un cliente…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} {c.lastname || ''} {c.cedula ? `(${c.cedula})` : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1">
              Notas <span className="text-gray-400">(opcional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones de la venta"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-900/10"
            />
          </div>

          <div className="flex flex-col gap-2 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={handleConfirmSale}
              disabled={isLoading}
              className={clsx(
                'w-full py-3 px-4 rounded-lg font-semibold text-white transition-colors',
                'bg-gray-900 hover:bg-gray-800',
                'disabled:opacity-50 disabled:cursor-not-allowed text-sm'
              )}
            >
              {isLoading ? 'Procesando…' : 'Confirmar venta'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isLoading}
              className="w-full py-2 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {step === 'success' && saleData && (
        <div className="space-y-6 text-center">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
            </div>
          </div>

          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-1">Venta registrada</h3>
            <p className="text-gray-600 text-sm">
              {saleResult?.id ? `Venta #${saleResult.id}` : 'La transacción se guardó correctamente.'}
            </p>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 text-left space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Producto:</span>
              <span className="font-semibold text-gray-900">{coffin.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Cantidad:</span>
              <span className="font-semibold text-gray-900">{saleData.quantity}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-2 mt-2">
              <span className="font-semibold text-gray-900">Total:</span>
              <span className="text-base font-semibold text-gray-900">
                ${(coffin.price * saleData.quantity).toLocaleString()}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-2 pt-4 border-t border-gray-200">
            {saleResult?.id && onDownloadReceipt && (
              <button
                type="button"
                onClick={handleDownload}
                className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-emerald-700 hover:bg-emerald-800 transition-colors text-sm inline-flex items-center justify-center gap-2"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Descargar recibo PDF
              </button>
            )}
            <button
              type="button"
              onClick={handleSellAnother}
              className="w-full py-3 px-4 rounded-lg font-semibold text-white bg-gray-900 hover:bg-gray-800 transition-colors text-sm"
            >
              Registrar otra venta
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="w-full py-2 px-4 rounded-lg font-medium text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default QuickSellModal;
